/*#
# Brief: a session's opening digest

What an agent needs when it starts work in a repo, in a few lines: what
changed since it last looked (tasks created or re-prioritised by someone
else), the open questions, what is in progress and by whom, and the top of
`ready`. `virta brief` prints it for the repo it runs in and remembers
where this identity left off; a Claude Code SessionStart hook running it
puts the board in the agent's context before it is asked anything (see
[For agents](/agents/)). It says nothing at all when the repo is not on the
board, so the hook is harmless everywhere.

`brief(store, { project, since })` is the pure part: `since` is the log
position this identity last saw — omitted on a first look, when "changed"
is empty rather than everything ever — and the result's `cursor` is the
one to pass next time. It is also the `brief` verb, so MCP has it.

`virta brief --stop` is a Claude Code Stop hook (board #3636): when a turn
would end with uncommitted or unpushed work in the repo, or a ready task on
its board, it asks the agent once to carry on (commit and push, then take
the task) instead of ending on "next up". `stopReason` is its pure part. It
lets the stop through when nothing is owed, when the agent has just asked
the owner a question on the board (an open `kind:question` it filed or
touched in the last half hour), and always on the second try in a turn, so
an agent that is answering the owner or waiting on them says so and stops.
It reads; it never writes the board.

```js
import { MemoryLog, VirtaStore, brief } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'agent' })
await store.load()
const other = new VirtaStore({ log: store.log, identity: 'owner' })
await other.load()
await store.create({ title: 'port the tests', tags: ['project:demo', 'status:ready'] })
const first = brief(store, { project: 'demo' })
await other.sync()
await other.create({ title: 'the build is red', tags: ['project:demo', 'priority:critical', 'status:ready'] })
await store.sync()
const next = brief(store, { project: 'demo', since: first.cursor })
preview.textContent = [
  `changed: ${next.changed.map((c) => `#${c.task.num} ${c.what.join(', ')}`).join('; ')}`,
  `ready: ${next.ready.map((t) => t.title).join(' | ')}`,
].join('\n')
```
```test
test('the second look shows what someone else changed, and ready is by priority', () => {
  expect(preview.textContent).toBe(
    // a priority set at creation is part of "created"; a later one is news
    'changed: #2 created\nready: the build is red | port the tests'
  )
})
```
*/

/*{ "parent": "Reference", "order": 62 }*/

import type { Task } from './model'
import { guide } from './guidance'
import { noticesFor } from './notices'
import type { VirtaStore } from './store'
import { ARCHIVED_TAG, priorityRank, tagValues, workersOf } from './tags'

export interface BriefChange {
  task: Task
  /** what happened, terse: `created`, `priority high`, `ready`, `2 comments`, `closed`, … */
  what: string[]
}

export interface Brief {
  project: string
  /** the board people open (the store's): where the brief's link points */
  board: string
  /**
   * false unless the project has enrolled itself (\`virta onboard\` in its
   * repo): then say nothing. Tasks others filed against it don't count —
   * no repo is surprised to find itself onboarded (board #1121).
   */
  onBoard: boolean
  /** the log position this brief covered up to: pass it as `since` next time */
  cursor: number
  since: number
  /** no `since` was given: "changed" is left empty on purpose */
  firstLook: boolean
  /**
   * what someone else did since `since`: created a task, re-prioritised it,
   * moved it to ready, commented, closed or reopened it — the things an
   * agent should hear about before it carries on (board #167)
   */
  changed: BriefChange[]
  /** open `kind:question` tasks: someone is waiting for an answer */
  questions: Task[]
  /** open `status:in-progress` tasks, with who is on them */
  inProgress: { task: Task; workers: string[] }[]
  /** the top of `status:ready`, by priority */
  ready: Task[]
  /** the Library's way in (board #2497): its root entry, and how many topics */
  guidance: { root: { num: number; title: string } | null; topics: number }
  /** ecosystem notices this project still owes (board #2548): how many, and the most important */
  notices: { owed: number; top: { num: number; title: string } | null }
  /**
   * owed notices that changed since `since` (board #2845): new to this
   * project, or reopened because a check changed. What the per-message
   * refresh says about notices — never the standing count.
   */
  noticeChanges: { num: number; title: string; why: 'new' | 'reopened' }[]
}

export interface BriefOptions {
  project: string
  /** the log position last seen; omitted is a first look (0 is "since the start") */
  since?: number
  /** whose changes not to report (default: the store's identity) */
  identity?: string
  /**
   * The caller's own credential (board #2733): a change stamped with this
   * token whose by-line names this project is the caller's own, however
   * its machine part is spelled (a hook's derived identity and an agent's
   * `--identity` or MCP `as` can differ). Not reported back as news.
   */
  token?: string
  /** at most this many per section (default 5) */
  limit?: number
}

/** The digest for one project: see the page above. */
export function brief(store: VirtaStore, options: BriefOptions): Brief {
  const { project } = options
  // no `since` is a first look; an explicit 0 is "since the log began"
  const firstLook = options.since === undefined
  const since = options.since ?? 0
  const identity = options.identity ?? store.identity
  const limit = options.limit ?? 5
  // archived:any: a task someone closed as rejected is archived, and that is
  // exactly the news "changed" must carry (board #753)
  const all = store.query(`project:${project} is:any archived:any`)
  // enrolled, not "has tasks": one outside ask must not switch on another
  // repo's workflow (board #1121)
  const onBoard = !!store.project(project).enrolled
  const changed: BriefChange[] = []
  // a first look reports the state, not every event ever
  if (!firstLook)
    for (const task of all) {
      const what: string[] = []
      let comments = 0
      for (const e of store.events(task.id)) {
        if (e.seq <= since || e.by === identity) continue
        if (
          options.token &&
          e.attribution?.token === options.token &&
          e.by.endsWith(` × ${project}`)
        )
          continue
        if (e.kind === 'created') what.push('created')
        if (e.kind === 'commented') comments++
        if (e.kind === 'closed') what.push('closed')
        if (e.kind === 'reopened') what.push('reopened')
        const tag = (e.payload as { tag?: string }).tag
        if (e.kind === 'tagged' && tag?.startsWith('priority:'))
          what.push(`priority ${tag.slice('priority:'.length)}`)
        if (e.kind === 'tagged' && tag === 'status:ready') what.push('ready')
      }
      if (comments)
        what.push(comments === 1 ? 'a comment' : `${comments} comments`)
      if (what.length) changed.push({ task, what })
    }
  changed.sort((a, b) => b.task.num - a.task.num)
  // "don't look at it" holds for the lists: an archived open task stays out
  const open = all.filter(
    (t) => t.closedAt === null && !t.tags.includes(ARCHIVED_TAG)
  )
  const byPriority = (a: Task, b: Task) =>
    priorityRank(a.tags) - priorityRank(b.tags) || a.num - b.num
  return {
    project,
    onBoard,
    cursor: store.cursor,
    since,
    firstLook,
    board: store.board,
    changed: changed.slice(0, limit),
    questions: open
      .filter((t) => tagValues(t.tags, 'kind').includes('question'))
      .sort(byPriority)
      .slice(0, limit),
    inProgress: open
      .filter((t) => tagValues(t.tags, 'status').includes('in-progress'))
      .sort(byPriority)
      .slice(0, limit)
      .map((task) => ({ task, workers: workersOf(task.tags) })),
    ready: open
      .filter((t) => tagValues(t.tags, 'status').includes('ready'))
      .sort(byPriority)
      .slice(0, limit),
    noticeChanges: firstLook
      ? []
      : noticesFor(store, project)
          .filter((l) => l.state === 'owed' || l.state === 'override-pending')
          .flatMap((l): Brief['noticeChanges'] => {
            const recent = store.events(l.task.id).filter((e) => e.seq > since)
            if (recent.some((e) => e.kind === 'created'))
              return [
                { num: l.task.num, title: l.task.title, why: 'new' as const },
              ]
            const reshaped = recent.some(
              (e) =>
                (e.kind === 'tagged' || e.kind === 'untagged') &&
                /^(check|targets|applies-if):/.test(e.payload.tag)
            )
            return reshaped
              ? [
                  {
                    num: l.task.num,
                    title: l.task.title,
                    why: 'reopened' as const,
                  },
                ]
              : []
          }),
    notices: (() => {
      const owed = noticesFor(store, project).filter(
        (l) => l.state === 'owed' || l.state === 'override-pending'
      )
      return {
        owed: owed.length,
        top: owed[0]
          ? { num: owed[0].task.num, title: owed[0].task.title }
          : null,
      }
    })(),
    guidance: (() => {
      const g = guide(store)
      return {
        root: g.root ? { num: g.root.num, title: g.root.title } : null,
        topics: g.topics.length,
      }
    })(),
  }
}

/**
 * A brief as a few lines of text — what a SessionStart hook puts in an
 * agent's context. Empty sections are left out; a quiet board is one line.
 */
export function renderBrief(b: Brief): string[] {
  const line = (t: Task) => {
    const priority = tagValues(t.tags, 'priority')[0]
    return `#${t.num}${priority ? ` [${priority}]` : ''} ${t.title}`
  }
  const lines = [
    `virta — ${b.project}${
      b.firstLook ? '' : ' (since you last looked)'
    }: the board is ${b.board}#?virta.scope=${encodeURIComponent(b.project)}`,
  ]
  const section = (title: string, items: string[]) => {
    if (!items.length) return
    lines.push(`  ${title}:`)
    for (const item of items) lines.push(`    ${item}`)
  }
  section(
    'changed',
    b.changed.map((c) => `${line(c.task)} — ${c.what.join(', ')}`)
  )
  section('questions waiting', b.questions.map(line))
  section(
    'in progress',
    b.inProgress.map(
      ({ task, workers }) =>
        `${line(task)}${workers.length ? ` — ${workers.join(', ')}` : ''}`
    )
  )
  section('ready', b.ready.map(line))
  if (lines.length === 1) lines.push('  nothing in progress or ready')
  // what the ecosystem asks of this repo, until it's done (board #2548)
  if (b.notices.owed && b.notices.top)
    lines.push(
      `  notices: ${b.notices.owed} owed — start with #${b.notices.top.num} ${b.notices.top.title} (\`virta project notices\` checks and records)`
    )
  // the way into the Library, when there is one (board #2497)
  if (b.guidance.root || b.guidance.topics)
    lines.push(
      `  guidance: \`virta guide\`${
        b.guidance.root
          ? ` — start at #${b.guidance.root.num} ${b.guidance.root.title}`
          : ''
      }${
        b.guidance.topics
          ? ` (${b.guidance.topics} topic${b.guidance.topics === 1 ? '' : 's'})`
          : ''
      }`
    )
  return lines
}

/**
 * Only what changed, for a hook that runs mid-session (`virta brief
 * --changes`, a UserPromptSubmit hook — board #167): nothing at all when
 * nothing changed, so the agent's context gets a line only when there is
 * news.
 */
export function renderChanges(b: Brief): string[] {
  if (!b.changed.length && !b.noticeChanges.length) return []
  return [
    `virta — since you last looked at ${b.project} (virta show #n to read one):`,
    ...b.changed.map(
      (c) => `  #${c.task.num} ${c.task.title} — ${c.what.join(', ')}`
    ),
    // a notice only when it's new here or a check changed (board #2845)
    ...b.noticeChanges.map(
      (n) =>
        `  notice #${n.num} ${n.title} — ${
          n.why === 'new' ? 'new, owed here' : 'its check changed: owed again'
        } (\`virta project notices\`)`
    ),
  ]
}

/**
 * The one-line footer a write gets when others changed something since this
 * identity last looked (board #142): `since you last looked: #88 priority
 * high; #90 created`. Empty when nothing changed — a write stays quiet.
 */
export function sinceLine(b: Brief): string {
  if (!b.changed.length) return ''
  return `since you last looked: ${b.changed
    .map((c) => `#${c.task.num} ${c.what.join(', ')}`)
    .join('; ')}`
}

/** What a Stop hook knows about the repo it runs in (board #3636). */
export interface RepoState {
  /** files `git status --porcelain` lists */
  uncommitted: number
  /** commits ahead of the upstream branch (0 when there is none) */
  unpushed: number
}

/** How recent a question must be to count as the one this turn asked. */
export const QUESTION_WINDOW_MS = 30 * 60 * 1000

/**
 * Why a turn shouldn't end yet, or null when it may (board #3636): the
 * reason a Stop hook returns to the agent as its next instruction. Ready
 * work is `status:ready` that is not itself a question and that nobody is
 * on. An open `kind:question` the agent (`identity`, or `token` writing as
 * this project) filed or touched within `QUESTION_WINDOW_MS` of `now`
 * means it is waiting on the owner: stop.
 */
export function stopReason(
  store: VirtaStore,
  b: Brief,
  repo: RepoState,
  options: { identity: string; token?: string; now?: number }
): string | null {
  const now = options.now ?? Date.now()
  const mine = (e: { by: string; attribution?: { token?: string } }) =>
    e.by === options.identity ||
    (!!options.token &&
      e.attribution?.token === options.token &&
      e.by.endsWith(` × ${b.project}`))
  const asked = store
    .query(`project:${b.project} kind:question`)
    .some((t) =>
      store
        .events(t.id)
        .some((e) => mine(e) && now - Date.parse(e.at) < QUESTION_WINDOW_MS)
    )
  if (asked) return null
  const ready = b.ready.filter(
    (t) =>
      !tagValues(t.tags, 'kind').includes('question') &&
      !workersOf(t.tags).length
  )
  const owed: string[] = []
  if (repo.uncommitted)
    owed.push(
      `${repo.uncommitted} uncommitted file(s) in this repo: commit them (or say in one line why they stay uncommitted)`
    )
  if (repo.unpushed) owed.push(`${repo.unpushed} commit(s) not pushed: push`)
  if (ready[0]) {
    const priority = tagValues(ready[0].tags, 'priority')[0]
    owed.push(
      `ready on the board: #${ready[0].num}${
        priority ? ` [${priority}]` : ''
      } ${ready[0].title}${
        ready.length > 1 ? ` (and ${ready.length - 1} more)` : ''
      } — take it now (\`virta show #${ready[0].num} --all\`, \`virta work #${
        ready[0].num
      }\`) rather than ending on "next up"`
    )
  }
  if (!owed.length) return null
  return `virta — before you stop: ${owed.join(
    '; '
  )}. If you are answering the owner, or waiting on their decision (file it as a kind:question card so the brief shows it), stopping is right: say so in one line and stop — this asks once per turn.`
}

/**
 * How many of a project's tasks its own agents filed (created by
 * `… × <project>`; board #3737): a repo using the board for itself, whether
 * or not it enrolled. Tasks others filed against it don't count (#1121).
 */
export function selfFiled(store: VirtaStore, project: string): number {
  return store
    .query(`project:${project} is:any archived:any`)
    .filter((t) => t.createdBy.endsWith(` × ${project}`)).length
}
